import {
  Activity,
  CheckCircle,
  ClipboardList,
  Clock,
  DollarSign,
  FileText,
  Hourglass,
  LayoutDashboard,
  Minus,
  TrendingUp,
  Upload,
  Wallet,
  XCircle,
} from "lucide-react";
import { useTranslation } from "@/shared/providers/LanguageProvider";
import { DashboardPageHeader } from "@/shared/ui/DashboardPageHeader";
import { DashboardSectionLabel } from "@/shared/ui/DashboardSectionLabel";
import { AuditorInvoiceDistributionChart } from "../components/auditor-dashboard/AuditorInvoiceDistributionChart";
import { AuditorProductivityIndicators } from "../components/auditor-dashboard/AuditorProductivityIndicators";
import { AuditorDashboardInvoicesSection } from "../components/auditor-dashboard/AuditorDashboardInvoicesSection";
import { DashboardMetricCard } from "../components/dashboard/DashboardMetricCard";
import { KpiGrid, type KpiTone } from "../components/KpiCard";
import { useAuditorDashboard } from "../hooks/useAuditorDashboard";
import { useDashboardSummary } from "../hooks/useDashboardSummary";
import { DashboardFiltersBar } from "../components/dashboard/DashboardFiltersBar";
import { DashboardStatusCards } from "../components/dashboard/DashboardStatusCards";
import { getRoleStatRoute } from "../navigation/dashboardRoutes";

const indicatorIcons = {
  file: FileText,
  upload: Upload,
  wallet: Wallet,
  clock: Clock,
  check: CheckCircle,
  x: XCircle,
  clipboard: ClipboardList,
};

const footerIcons = {
  minus: Minus,
  hourglass: Hourglass,
  dollar: DollarSign,
  activity: Activity,
  trending: TrendingUp,
  x: XCircle,
};

const auditorMetricConfig: Record<string, KpiTone> = {
  invoices: "sky",
  submitted: "amber",
  review: "purple",
  approved: "mint",
  paid: "lime",
  rejected: "red",
  validation: "pink",
};

export function AuditorDashboardPage() {
  const { t } = useTranslation();
  const labels = t.safecheck.dashboard.auditorBoard;
  const { data, loading, error, usingApi } = useAuditorDashboard();
  // Filtres en haut de page et accès rapide : 5 cartes de factures, 4 cartes de demandes.
  const { filters, setFilters, summary, error: summaryError } = useDashboardSummary("auditor");

  const subtitle = labels.subtitle
    .replace("{invoices}", String(data.header.invoicesToAudit))
    .replace("{requests}", String(data.header.pendingRequests));

  return (
    <div className="space-y-5">
      <DashboardPageHeader
        title={labels.pageTitle}
        subtitle={subtitle}
        icon={LayoutDashboard}
        badge={data.header.roleBadge}
        date={data.header.displayDate}
      />

      <DashboardFiltersBar filters={filters} options={summary?.options ?? null} onChange={setFilters} />
      {loading && <p className="text-sm text-muted">Chargement…</p>}
      {error && <p className="text-sm text-danger">{error}</p>}
      {summaryError && <p className="text-sm text-danger">{summaryError}</p>}

      <div>
        <DashboardSectionLabel>{labels.sections.quickAccess}</DashboardSectionLabel>
        <DashboardStatusCards summary={summary} />
      </div>
      {usingApi && !loading && (
        <p className="text-xs text-muted">Données synchronisées depuis l&apos;API.</p>
      )}

      <div>
        <DashboardSectionLabel>{labels.sections.indicators}</DashboardSectionLabel>
        <KpiGrid className="grid-cols-1 md:grid-cols-2 xl:grid-cols-4">
          {data.indicators.map((indicator) => {
            const Icon = indicatorIcons[indicator.icon];
            const FooterIcon = footerIcons[indicator.footerIcon];
            const label = labels.indicators[indicator.id as keyof typeof labels.indicators];
            const footer =
              labels.indicatorFooters[indicator.footerKey as keyof typeof labels.indicatorFooters];
            const tone = auditorMetricConfig[indicator.id] ?? "slate";

            return (
              <DashboardMetricCard
                key={indicator.id}
                title={label}
                value={indicator.value}
                footer={
                  <span className="inline-flex items-center gap-1">
                    <FooterIcon size={12} />
                    {footer}
                  </span>
                }
                icon={Icon}
                href={getRoleStatRoute("auditor", indicator.id)}
                tone={tone}
              />
            );
          })}
        </KpiGrid>
      </div>

      <div>
        <DashboardSectionLabel>{labels.sections.charts}</DashboardSectionLabel>
        <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
          <AuditorInvoiceDistributionChart />
          <AuditorProductivityIndicators />
        </div>
      </div>

      <div>
        <DashboardSectionLabel>{labels.sections.invoices}</DashboardSectionLabel>
        <AuditorDashboardInvoicesSection invoices={data.submittedInvoices} />
      </div>
    </div>
  );
}
